import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft, Upload, Download, Trash2, Clock, Database } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useOsMe, OsStatusBadge, OsDenied, OS_STATUS_FLOW, OS_STATUS_LABEL, OS_EVENT_LABEL, OS_WRITE_ROLES } from "./osShared";
import { TransitionBar, DecisionModal, DecisionCard, AssigneePicker, DueBadge } from "./caseWorkflow";
import LeafletMap from "./LeafletMap";

const DOC_TYPES = ["ANSÖKAN", "RITNING", "BESLUT", "ÖVRIGT"];

export default function OsCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me, denied } = useOsMe();
  const [c, setC] = useState(null);
  const [property, setProperty] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("RITNING");
  const [transitions, setTransitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [decisionOpen, setDecisionOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/os/cases/${id}`);
      setC(data);
      api.get(`/os/properties/${data.propertyId}`).then((r) => setProperty(r.data)).catch(() => {});
      api.get(`/os/cases/${id}/timeline`).then((r) => setTimeline(r.data.entries)).catch(() => {});
      api.get(`/os/documents?caseId=${id}&limit=100`).then((r) => setDocs(r.data.items)).catch(() => {});
      api.get(`/os/cases/${id}/transitions`).then((r) => setTransitions(r.data.transitions)).catch(() => setTransitions([]));
    } catch {
      toast.error("Kunde inte hämta ärendet");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (me && OS_WRITE_ROLES.includes(me.role)) {
      api.get("/os/users?limit=200").then((r) => setUsers(r.data.items)).catch(() => {});
    }
  }, [me]);

  if (denied) return <OsDenied />;
  if (!c) return <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;

  const canWrite = me && OS_WRITE_ROLES.includes(me.role);

  const transition = async (to, decision = null) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/os/cases/${id}/transition`, { toStatus: to, decision });
      setC(data);
      setDecisionOpen(false);
      toast.success(`Status: ${OS_STATUS_LABEL[to]}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.failures || []).map((f) => f.message).join("; ") || d.message : d || "Transition nekades");
    } finally {
      setBusy(false);
    }
  };

  const onTransition = (t) => {
    if (t.requiresDecision) setDecisionOpen(true);
    else transition(t.to);
  };

  const assign = async (assigneeId) => {
    try {
      const { data } = await api.post(`/os/cases/${id}/assign`, { assigneeId });
      setC(data);
      toast.success(assigneeId ? "Ärendet tilldelat" : "Tilldelning borttagen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte tilldela");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/os/documents?type=${encodeURIComponent(docType)}&caseId=${id}`, fd);
      toast.success("Dokument uppladdat");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const download = async (d) => {
    const res = await api.get(`/os/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = d.filename || d.id; a.click();
    URL.revokeObjectURL(url);
  };

  const removeDoc = async (d) => {
    await api.delete(`/os/documents/${d.id}`);
    toast.success("Dokument borttaget");
    load();
  };

  const removeCase = async () => {
    try {
      await api.delete(`/os/cases/${id}`);
      toast.success("Ärendet borttaget");
      navigate("/os");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kan inte tas bort");
    }
  };

  return (
    <div className="max-w-5xl mx-auto animate-fade-in-up" data-testid="os-case-detail">
      <Link to="/os" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-blue-700">
        <ChevronLeft className="w-4 h-4" /> Alla ärenden
      </Link>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-mono" style={{ fontFamily: "Outfit" }} data-testid="os-case-number">{c.caseNumber}</h1>
          <p className="text-sm text-zinc-600">{property ? property.designation : c.propertyId}{property?.areaSqM ? ` · ${property.areaSqM} m²` : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DueBadge dueDate={c.dueDate} status={c.status} />
          <OsStatusBadge status={c.status} />
        </div>
      </div>

      {/* Tilldelning */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <AssigneePicker caseData={c} users={users} onAssign={assign} canWrite={canWrite} />
        {c.submittedAt && <span className="text-[11px] text-zinc-400">Inlämnat {(c.submittedAt || "").slice(0, 10)}{c.dueDate ? ` · Beslutsfrist ${(c.dueDate || "").slice(0, 10)} (PBL 9:27)` : ""}</span>}
      </div>

      {/* Statuskedja */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5">
        <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="os-status-chain">
          {OS_STATUS_FLOW.map((s, i) => {
            const reached = OS_STATUS_FLOW.indexOf(c.status) >= i;
            return (
              <React.Fragment key={s}>
                {i > 0 && <div className={`h-0.5 w-6 shrink-0 ${reached ? "bg-blue-700" : "bg-zinc-200"}`} />}
                <div className={`px-2.5 py-1 text-[11px] font-semibold rounded-sm whitespace-nowrap ${s === c.status ? "bg-blue-700 text-white" : reached ? "bg-blue-50 text-blue-800" : "bg-zinc-100 text-zinc-400"}`}>
                  {OS_STATUS_LABEL[s]}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {canWrite && transitions.length > 0 && (
          <>
            <TransitionBar transitions={transitions} onTransition={onTransition} busy={busy} />
            {c.status === "DRAFT" && (
              <div className="mt-2">
                <button onClick={removeCase} data-testid="os-case-delete"
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-700 hover:bg-red-50 text-sm rounded-sm">
                  <Trash2 className="w-3.5 h-3.5" /> Ta bort (utkast)
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <DecisionCard decision={c.decision} />
      <DecisionModal open={decisionOpen} onClose={() => setDecisionOpen(false)} busy={busy}
        onSubmit={(d) => transition("DECIDED", d)} />

      {/* Karta */}
      {property?.geometry?.type === "Point" && (
        <div className="mt-5 bg-white border border-zinc-200 rounded-sm p-5" data-testid="os-case-map">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">Fastighetens läge</h3>
          <LeafletMap
            markers={[{ lat: property.geometry.coordinates[1], lng: property.geometry.coordinates[0], label: property.designation }]}
            height="h-[300px]"
            testid="os-case-map-canvas"
          />
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Dokument */}
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Dokument ({docs.length})</h3>
            <div className="flex items-center gap-2">
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="os-doc-type"
                className="px-2 py-1.5 text-xs border border-zinc-300 rounded-sm outline-none">
                {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white text-xs rounded-sm cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> Ladda upp
                <input type="file" className="hidden" onChange={upload} data-testid="os-doc-upload" />
              </label>
            </div>
          </div>
          <div className="mt-3 divide-y divide-zinc-100" data-testid="os-doc-list">
            {docs.length === 0 && <p className="text-sm text-zinc-400 py-2">Inga dokument.</p>}
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between py-2">
                <div>
                  <div className="text-sm text-zinc-800">{d.filename || d.id}</div>
                  <div className="text-[11px] text-zinc-400">{d.type} · {d.processingStatus}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => download(d)} title="Ladda ner" data-testid={`os-doc-download-${d.id}`}
                    className="p-1.5 text-zinc-500 hover:text-blue-700"><Download className="w-4 h-4" /></button>
                  {canWrite && (
                    <button onClick={() => removeDoc(d)} title="Ta bort" data-testid={`os-doc-delete-${d.id}`}
                      className="p-1.5 text-zinc-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tidslinje */}
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Tidslinje</h3>
          <div className="mt-3 space-y-3" data-testid="os-timeline">
            {timeline.length === 0 && <p className="text-sm text-zinc-400">Inga händelser.</p>}
            {timeline.map((e, i) => (
              <div key={i} className="flex gap-3">
                <div className={`mt-0.5 w-7 h-7 shrink-0 rounded-sm flex items-center justify-center ${e.kind === "event" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                  {e.kind === "event" ? <Clock className="w-3.5 h-3.5" /> : <Database className="w-3.5 h-3.5" />}
                </div>
                <div>
                  <div className="text-sm font-medium text-zinc-800">
                    {e.kind === "event" ? (OS_EVENT_LABEL[e.data.eventType] || e.data.eventType) : `Proveniens: ${e.data.attributeName}`}
                  </div>
                  <div className="text-[11px] text-zinc-400">
                    {(e.at || "").slice(0, 16).replace("T", " ")}
                    {e.kind === "event" && e.data.payload?.to ? ` · ${e.data.payload.from} → ${e.data.payload.to}` : ""}
                    {e.kind === "event" && e.data.payload?.outcome ? ` · ${e.data.payload.outcome}` : ""}
                    {e.kind === "event" && e.data.eventType === "CASE_ASSIGNED" ? ` · ${e.data.payload?.assigneeEmail || "tilldelning borttagen"}` : ""}
                    {e.kind === "provenance" ? ` · källa: ${e.data.source}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
